import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2 } from "lucide-react";
import { PageHead, Card } from "@/pages/app/AppShell";
import { api, errMsg, money, fmtDate } from "@/lib/api";

const STATUS = {
  pending_review: ["Waiting for approval", "bg-amber-300/15 text-amber-300"],
  live: ["Live", "bg-emerald-400/15 text-emerald-300"],
  ended: ["Ended", "bg-[#2A1240]/5 text-slate-300"],
  rejected: ["Not approved · refunded", "bg-red-400/10 text-red-300"],
};

const EMPTY = { title: "", text: "", cta_label: "Learn more", link: "", image_url: "", weeks: 1 };

function AdForm({ price, onBooked }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: k === "weeks" ? Number(e.target.value) : e.target.value });
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ads/image", fd);
      setF((x) => ({ ...x, image_url: data.image_url }));
    } catch (e2) { toast.error(errMsg(e2)); } finally { setUploading(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/ads", { ...f, origin_url: window.location.origin });
      onBooked();
      window.location.href = data.checkout_url;
    } catch (e2) { toast.error(errMsg(e2)); setBusy(false); }
  };
  const img = f.image_url ? `${process.env.REACT_APP_BACKEND_URL}${f.image_url}` : "/img/summit.jpg";
  return (
    <form onSubmit={submit} className="grid gap-6 lg:grid-cols-2" data-testid="ad-form">
      <div className="space-y-4">
        <input className="field" required minLength={3} maxLength={80} placeholder="Headline, e.g. Looking for a summer festival sponsor" value={f.title} onChange={set("title")} data-testid="ad-title-input" />
        <textarea className="field min-h-[96px]" required minLength={10} maxLength={240} placeholder="One or two sentences about what you offer or need" value={f.text} onChange={set("text")} data-testid="ad-text-input" />
        <div className="grid grid-cols-2 gap-3">
          <input className="field" required maxLength={30} placeholder="Button text" value={f.cta_label} onChange={set("cta_label")} data-testid="ad-cta-input" />
          <input className="field" required placeholder="Link, e.g. /companies/... or https://" value={f.link} onChange={set("link")} data-testid="ad-link-input" />
        </div>
        <label className="btn-ghost w-full cursor-pointer" data-testid="ad-image-upload">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}{f.image_url ? "Change picture" : "Upload a picture"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
        <div className="flex items-center gap-3">
          <select className="field !w-auto" value={f.weeks} onChange={set("weeks")} data-testid="ad-weeks-select">
            {[1, 2, 3, 4].map((w) => <option key={w} value={w}>{`${w} week${w > 1 ? "s" : ""}`}</option>)}
          </select>
          <p className="text-sm text-slate-300" data-testid="ad-total">Total <span className="font-semibold text-[#2A1240]">{money(price * f.weeks, 0)}</span></p>
        </div>
        <button className="btn-gold w-full" disabled={busy || uploading} data-testid="ad-pay-button">{busy ? "Opening checkout…" : `Pay ${money(price * f.weeks, 0)} and submit`}</button>
        <p className="text-xs text-slate-400">We review every ad before it goes live, usually within a day. If we can't approve it, you get a full refund.</p>
      </div>
      <div data-testid="ad-preview">
        <p className="text-xs text-slate-400 mb-2">Preview</p>
        <div className="overflow-hidden rounded-2xl border border-[#2A1240]/10 bg-white">
          <div className="relative aspect-[16/9]"><img src={img} alt="" className="absolute inset-0 h-full w-full object-cover" /><span className="absolute left-4 top-4 rounded-md bg-white/90 px-2.5 py-1 text-xs font-semibold text-[#2A1240]">Featured</span></div>
          <div className="p-6">
            <h3 className="font-display text-2xl text-[#2A1240]">{f.title || "Your headline"}</h3>
            <p className="mt-2 text-sm text-slate-300">{f.text || "Your short description appears here."}</p>
            <span className="btn-gold mt-4 !py-2">{f.cta_label || "Learn more"}</span>
          </div>
        </div>
      </div>
    </form>
  );
}

const AdRow = ({ a }) => {
  const [label, cls] = STATUS[a.status] || [a.status, ""];
  return (
    <div className="py-4 flex flex-wrap items-center justify-between gap-3" data-testid={`my-ad-${a.id}`}>
      <div>
        <p className="text-[#2A1240]">{a.title}</p>
        <p className="text-xs text-slate-400">{a.weeks} week{a.weeks > 1 ? "s" : ""} · {money(a.amount, 0)}{a.starts_at ? ` · ${fmtDate(a.starts_at)} – ${fmtDate(a.ends_at)}` : ""}{a.reject_reason ? ` · ${a.reject_reason}` : ""}</p>
      </div>
      <span className={`rounded-full px-3 py-1 text-xs ${cls}`}>{label}</span>
      {a.stats && ["live", "ended"].includes(a.status) && (
        <dl className="w-full grid grid-cols-4 gap-2 text-center" data-testid={`my-ad-stats-${a.id}`}>
          {[["Views", a.stats.views], ["People reached", a.stats.viewers], ["Clicks", a.stats.clicks], ["Click rate", `${a.stats.ctr}%`]].map(([k, v]) => (
            <div key={k} className="rounded-lg bg-[#FBE9F3] px-2 py-2">
              <dt className="text-[11px] text-slate-300">{k}</dt>
              <dd className="font-display text-lg text-[#2A1240]">{v}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
};

export default function Promote() {
  const [price, setPrice] = useState(49);
  const [ads, setAds] = useState([]);
  const load = () => api.get("/ads/mine").then((r) => setAds(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => {
    api.get("/ads/pricing").then((r) => setPrice(r.data?.week_price ?? 49)).catch(() => {});
    load();
  }, []);
  return (
    <div className="space-y-8" data-testid="promote-page">
      <PageHead kicker="Promote" title="Book a Featured ad on the home page" />
      <p className="text-slate-300 -mt-4">Two Featured spots sit right under the home page header, the first thing every visitor sees. {money(price, 0)} per week.</p>
      <Card><AdForm price={price} onBooked={load} /></Card>
      <Card>
        <h2 className="kicker">Your ads</h2>
        {ads.length ? <div className="mt-2 divide-y divide-[#2A1240]/10">{ads.map((a) => <AdRow key={a.id} a={a} />)}</div>
          : <p className="mt-3 text-sm text-slate-400" data-testid="my-ads-empty">You haven't booked an ad yet.</p>}
      </Card>
    </div>
  );
}
